<template>
    <div class="venue-management">
        <div class="header-section">
            <h2>Venue Management</h2>
            <button @click="showAddVenueModal = true" class="add-btn">Add New Venue</button>
        </div>

        <!-- Venues List -->
        <div class="venues-list">
            <div v-for="venue in venues" :key="venue.id" class="venue-item">
                <div class="venue-info">
                    <h3 class="venue-name">{{ venue.name }}</h3>
                    <div class="venue-details">
                        <span class="address">{{ venue.address }}</span>
                        <span class="separator">•</span>
                        <span class="capacity" v-if="venue.capacity">
                            Capacity: {{ venue.capacity }} people
                        </span>
                        <span class="separator">•</span>
                        <span class="contact" v-if="venue.contact_info">
                            {{ venue.contact_info }}
                        </span>
                    </div>
                </div>
                <div class="venue-actions">
                    <button @click="editVenue(venue)" class="edit-btn">Edit</button>
                    <button @click="confirmDelete(venue)" class="delete-btn">Delete</button>
                </div>
            </div>
        </div>

        <!-- Add/Edit Venue Modal -->
        <div v-if="showAddVenueModal" class="modal">
            <div class="modal-content">
                <h3>{{ editingVenue ? 'Edit Venue' : 'Add New Venue' }}</h3>
                <form @submit.prevent="saveVenue">
                    <div class="form-section">
                        <div class="form-group">
                            <label>Venue Name</label>
                            <input 
                                v-model="venueForm.name" 
                                type="text" 
                                required
                                placeholder="Enter venue name"
                            >
                        </div>

                        <div class="form-group">
                            <label>Address</label>
                            <textarea 
                                v-model="venueForm.address" 
                                required
                                rows="3"
                                placeholder="Enter full address"
                            ></textarea>
                        </div>

                        <div class="form-group">
                            <label>Capacity</label>
                            <input 
                                v-model="venueForm.capacity" 
                                type="number"
                                min="1"
                                placeholder="Number of people"
                            >
                        </div>

                        <div class="form-group">
                            <label>Contact Information</label>
                            <textarea 
                                v-model="venueForm.contact_info"
                                rows="2"
                                placeholder="Contact details for venue"
                            ></textarea>
                        </div>
                    </div>

                    <div class="modal-actions">
                        <button type="submit" class="save-btn">
                            {{ editingVenue ? 'Update' : 'Add' }} Venue
                        </button>
                        <button type="button" @click="closeModal" class="cancel-btn">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { eventService } from '../services/eventService';

export default {
    name: 'VenueManagement',
    setup() {
        const venues = ref([]);
        const showAddVenueModal = ref(false);
        const editingVenue = ref(null);
        const error = ref('');

        const defaultVenueForm = {
            name: '',
            address: '',
            capacity: '',
            contact_info: ''
        };

        const venueForm = ref({ ...defaultVenueForm });

        const loadVenues = async () => {
            try {
                const response = await eventService.getAllVenues();
                venues.value = response;
            } catch (err) {
                console.error('Error loading venues:', err);
                error.value = 'Failed to load venues';
            }
        };

        const saveVenue = async () => {
            try {
                if (editingVenue.value) {
                    await eventService.updateVenue(editingVenue.value.id, venueForm.value);
                } else {
                    await eventService.createVenue(venueForm.value);
                }
                await loadVenues();
                closeModal();
            } catch (err) {
                console.error('Error saving venue:', err);
                error.value = 'Failed to save venue';
            }
        };

        const editVenue = (venue) => {
            editingVenue.value = venue;
            venueForm.value = { ...venue };
            showAddVenueModal.value = true;
        };

        const confirmDelete = async (venue) => {
            if (confirm(`Are you sure you want to delete ${venue.name}?`)) {
                try {
                    await eventService.deleteVenue(venue.id);
                    await loadVenues();
                } catch (err) {
                    console.error('Error deleting venue:', err);
                    error.value = 'Failed to delete venue';
                }
            }
        };

        const closeModal = () => {
            showAddVenueModal.value = false;
            editingVenue.value = null;
            venueForm.value = { ...defaultVenueForm };
            error.value = '';
        };

        onMounted(loadVenues);

        return {
            venues,
            showAddVenueModal,
            venueForm,
            editingVenue,
            error,
            saveVenue,
            editVenue,
            confirmDelete,
            closeModal
        };
    }
};
</script>

<style scoped>
.venue-management {
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
}

.add-btn {
    background-color: #4CAF50;
    color: white;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.venues-list {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    overflow: hidden;
}

.venue-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #eee;
}

.venue-item:last-child {
    border-bottom: none;
}

.venue-info {
    flex: 1;
    text-align: left;
}

.venue-name {
    font-size: 1.1rem;
    color: #2c3e50;
    margin: 0 0 0.25rem 0;
    text-align: left;
}

.venue-details {
    color: #666;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-align: left;
}

.separator {
    color: #ccc;
}

.venue-actions {
    display: flex;
    gap: 0.5rem;
}

.edit-btn {
    background-color: #3498db;
    color: white;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.delete-btn {
    background-color: #dc3545;
    color: white;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

/* Modal styles from AdminPanel.vue */
.modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 2rem;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 600px;
}

.form-section {
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 4px;
    margin-bottom: 1.5rem;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.form-group input,
.form-group textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
}

.save-btn {
    background-color: #4CAF50;
    color: white;
    padding: 0.5rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.cancel-btn {
    background-color: #6c757d;
    color: white;
    padding: 0.5rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}
</style> 